import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import IconSARs from '@persona-id/design-tokens/icons/GovernmentIcon';
import { RangeValue } from '@react-types/shared';
import { DateTime } from 'luxon';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import Pagination from 'dashboard/components/Pagination';
import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import { generatePlaceHolderDisplay } from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import DashboardDatePicker from 'dashboard/components-lib/DashboardDatePicker/DashboardDatePicker';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { SAR, SAR_STATUS_LABEL_MAP, SARStatus } from 'dashboard/models/sar';
import { SARsFilterParams } from 'dashboard/redux/slices/sars';
import SARsTable from 'dashboard/views/DashboardSARsView/SARsTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import Tooltip from 'design-system/components/Tooltip';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const SearchButtonRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr minmax(300px, 1fr);
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const FilterRow = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: auto auto;
  }
`;

const customStyles = {
  control: () => ({
    minHeight: Spacing[6],
  }),
  container: () => ({
    minHeight: Spacing[6],
  }),
  valueContainer: () => ({
    height: Spacing[6],
  }),
  indicatorsContainer: () => ({
    height: Spacing[6],
  }),
};

const STATUS_FILTER_OPTIONS = Object.values(SARStatus).map((value) => ({
  label: SAR_STATUS_LABEL_MAP[value],
  value,
}));

export interface DashboardSARsViewProps {
  loadingSARs: boolean;
  productFeatureIsDisabled: boolean;
  sars: SAR[];
  paginationParams: PaginationParams;
  sortParams: SortParams;
  filters: SARsFilterParams;
  pageSize: number;
  handleCreateSAR: () => void;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleStatusFilterUpdate: (sarStatuses: SARStatus[]) => void;
}

const DashboardSARsView = ({
  loadingSARs,
  sars,
  filters,
  productFeatureIsDisabled,
  paginationParams,
  sortParams,
  pageSize,
  handleCreateSAR,
  handleDateUpdate,
  handlePageLinkClick,
  handlePageSizeClick,
  handleOmniSearch,
  handleSortUpdate,
  handleStatusFilterUpdate,
}: DashboardSARsViewProps) => {
  const { createdAtStart, createdAtEnd, status, omni } = filters;

  const hasSARCreatePermission = usePermission(Permission.SARCreate);

  const hasSARConfigurationViewPermissions = usePermission(Permission.SARConfigurationView);

  if (productFeatureIsDisabled) {
    return (
      <>
        <DisabledProductFeature
          title='SAR Filings'
          prompt='Empower your compliance team to easily create and submit suspicious activity reports from Persona.'
          helpLink={
            <a
              href='https://support.withpersona.com/hc/en-us/categories/360004567574-Product-guide'
              target='_blank'
              rel='noreferrer'
            >
              Learn more about SAR Filings
            </a>
          }
          icon={<IconSARs />}
          popupMessage={
            productFeatureIsDisabled
              ? 'Your current plan does not have access to this feature.'
              : 'Your current role does not have permissions for this feature'
          }
        />
      </>
    );
  }

  return (
    <Container>
      <InactiveEnvironmentBanner />
      <Header title='SAR Filings'>
        <HStack>
          {hasSARCreatePermission ? (
            <Button
              id='sars-view__create-sar'
              variant='basic'
              onClick={handleCreateSAR}
              prefixIcon={<PlusIcon />}
            >
              Create SAR
            </Button>
          ) : (
            <Tooltip
              trigger={
                <Button
                  id='sars-view__create-sar'
                  variant='basic'
                  prefixIcon={<PlusIcon />}
                  disabled
                >
                  Create SAR
                </Button>
              }
            >
              Your role does not have permission to create SARs.
            </Tooltip>
          )}
          {/* [chris] todo: we need this hack because even when a button is disabled, the 'to' link still works. */}
          {hasSARConfigurationViewPermissions ? (
            <Button
              id='sars-view__sar-org-configuration'
              to='/dashboard/sar-configurations'
              variant='basic'
            >
              Settings
            </Button>
          ) : (
            <Tooltip
              trigger={
                <Button id='sars-view__sar-org-configuration' variant='basic' disabled>
                  Settings
                </Button>
              }
            >
              Your role does not have permission to access this page.
            </Tooltip>
          )}
        </HStack>
      </Header>
      <Section>
        <Card>
          <Card.Body>
            <SearchButtonRow>
              <SearchInput
                id='sars__search'
                handleInput={(value: string) => {
                  handleOmniSearch(value);
                }}
                value={omni || ''}
                placeholder='Search by SAR ID, name, or BSA ID'
                size='md'
              />
              <DashboardDatePicker
                dataTest='sars__date-range'
                handleDatesChange={handleDateUpdate}
                dateRange={{
                  startDate: createdAtStart,
                  endDate: createdAtEnd,
                }}
                label={`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
                size='md'
              />
            </SearchButtonRow>
            <FilterRow id='sars-table__filters'>
              <div id='sars-view__status'>
                <FormControlLabel>Status</FormControlLabel>
                <ReactMultiSelect
                  id='sars__select-status'
                  aria-label='Status'
                  value={status || []}
                  styles={customStyles}
                  placeholderGenerator={generatePlaceHolderDisplay({
                    label: { single: 'status', multiple: 'statuses' },
                    emptyIsAll: true,
                  })}
                  options={STATUS_FILTER_OPTIONS}
                  onChange={(value) => handleStatusFilterUpdate(value as SARStatus[])}
                  selectAllEnabled
                  searchEnabled
                />
              </div>
            </FilterRow>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <Card.Title>SAR Filings</Card.Title>
            <SARsTable
              sars={sars}
              hasUsedFilters={false}
              sortParams={sortParams}
              isLoading={loadingSARs}
              handleSortUpdate={handleSortUpdate}
            />
            <Pagination
              pageSize={pageSize}
              paginationParams={paginationParams}
              handlePageSizeClick={handlePageSizeClick}
              handlePageLinkClick={handlePageLinkClick}
            />
          </Card.Body>
        </Card>
      </Section>
    </Container>
  );
};

export default DashboardSARsView;
